import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import cn from 'classnames';
import { IconButton } from '@asphalt-react/button';
import { Cross, CaretDown, CaretUp } from '@asphalt-react/iconpack';
import { propsUtil } from '@asphalt-react/helper';
import matchSorter from 'match-sorter';
import { Manager, Reference, Popper } from 'react-popper';
import Checkbox from '@asphalt-react/checkbox';
import Avatar from '@asphalt-react/avatar';
import Tag from '@asphalt-react/tag';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Dropdown__3uVFl {\n  --dropdown-font-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --dropdown-hover-color: var(--interactive-fill-hover, hsla(216, 24%, 96%, 1));\n  --dropdown-icon-color: var(--icon-primary, hsla(220, 42%, 24%, 1));\n  position: relative;\n  width: 320px;\n  box-sizing: border-box;\n}\n.Dropdown__3uVFl.stretch__16UHO {\n  width: 100%;\n}\n.l__ImR8M {\n  font-size: var(--body-l, 1.13rem);\n}\n.m__2iTTe {\n  font-size: var(--body-m, 1rem);\n}\n.s__2f0O_ {\n  font-size: var(--body-s, 0.88rem);\n}\n.SelectionBox__1ZdbJ {\n  border: var(--border-width, 1px)\n    var(--interactive-border-solid, solid hsla(220, 22%, 76%, 1));\n  border-radius: var(--roundness-1, 2px);\n  line-height: initial;\n  color: var(--dropdown-font-color);\n  display: inline-flex;\n  align-items: center;\n  justify-content: space-between;\n  width: 100%;\n  outline: none;\n  background-color: var(--interactive-fill-default, hsla(0, 0%, 100%, 1));\n  cursor: default;\n  box-sizing: border-box;\n}\n\n.SelectionBox__1ZdbJ.s__2f0O_ {\n  min-height: 32px;\n  padding: 0.187rem 0.75rem;\n}\n.SelectionBox__1ZdbJ.m__2iTTe {\n  min-height: 40px;\n  padding: 0.28rem 0.75rem;\n}\n.SelectionBox__1ZdbJ.l__ImR8M {\n  min-height: 48px;\n  padding: 0.375rem 0.75rem;\n}\n\n.SelectionBox__1ZdbJ:hover {\n  background-color: var(--interactive-fill-hover, hsla(216, 24%, 96%, 1));\n}\n.SelectionBox__1ZdbJ:focus,\n.SelectionBox__1ZdbJ:focus-within {\n  background-color: var(--interactive-fill-hover, hsla(216, 24%, 96%, 1));\n  border-color: var(--interactive-focus, hsla(219, 42%, 18%, 1));\n}\n.SelectionBox__1ZdbJ.invalid__1Jp2p {\n  border-color: var(--interactive-border-support, hsla(12, 92%, 36%, 1));\n}\n.SelectionBox__1ZdbJ.placeholder__2D_EI {\n  color: var(--text-muted, hsla(219, 23%, 60%, 1));\n}\n.SelectionBox__1ZdbJ.disabled__3u096 {\n  background-color: var(--interactive-disabled, hsla(0, 0%, 86%, 1));\n  color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n  border: none;\n  pointer-events: none;\n}\n.Dropdown__3uVFl.disabled__3u096 {\n  cursor: not-allowed;\n}\n.selection__V6aN8 {\n  overflow: auto;\n}\n.inputFieldWrap__3KO_v {\n  width: 100%;\n}\n.inputField__1bsIH {\n  width: 100%;\n  background: transparent;\n  border: none;\n  outline: none;\n  font-size: inherit;\n}\n.inputField__1bsIH::-moz-placeholder {\n  color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  opacity: 1;\n}\n.inputField__1bsIH:-ms-input-placeholder {\n  color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  opacity: 1;\n}\n.inputField__1bsIH::placeholder {\n  color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  opacity: 1;\n}\n.inputField__1bsIH.applyTextColor__2iGvN::-moz-placeholder {\n  color: var(--dropdown-font-color);\n  opacity: 1;\n}\n.inputField__1bsIH.applyTextColor__2iGvN:-ms-input-placeholder {\n  color: var(--dropdown-font-color);\n  opacity: 1;\n}\n.inputField__1bsIH.applyTextColor__2iGvN::placeholder {\n  color: var(--dropdown-font-color);\n  opacity: 1;\n}\n.inputField__1bsIH:focus::-moz-placeholder {\n  color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  opacity: 1;\n}\n.inputField__1bsIH:focus:-ms-input-placeholder {\n  color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  opacity: 1;\n}\n.inputField__1bsIH:focus::placeholder {\n  color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  opacity: 1;\n}\n.inputField__1bsIH:disabled::-moz-placeholder {\n  color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n}\n.inputField__1bsIH:disabled:-ms-input-placeholder {\n  color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n}\n.inputField__1bsIH:disabled::placeholder {\n  color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n}\n.iconsContainer__badVK {\n  display: inline-flex;\n  align-items: center;\n}\n.iconsContainer__badVK svg path {\n  fill: var(--dropdown-icon-color);\n}\n\n.Popover__2dW1s {\n  --popover-border-radius: var(--roundness-1, 2px);\n  background-color: var(--background-fill-primary, hsla(0, 0%, 100%, 1));\n  width: 100%;\n  padding: 5px 0;\n  z-index: 1;\n  overflow: hidden;\n  box-sizing: border-box;\n}\n.Popover__2dW1s.popBot__fVRh6 {\n  border-bottom-left-radius: var(--popover-border-radius);\n  border-bottom-right-radius: var(--popover-border-radius);\n  box-shadow: var(--shadow-low, 0px 0px 10px 0px hsla(222, 42%, 6%, 0.1));\n}\n.Popover__2dW1s.popTop__BbZTb {\n  border-top-left-radius: var(--popover-border-radius);\n  border-top-right-radius: var(--popover-border-radius);\n  box-shadow: 0 0px 6px 0 hsla(222, 4%, 6%, 0.1);\n}\n.caretIcon__3itFy {\n  font-size: 0;\n}\n.SelectionBox__1ZdbJ.disabled__3u096 .caretIcon__3itFy path {\n  fill: var(--icon-disabled, hsla(0, 0%, 62%, 1));\n}\n.emptyState__3bMvZ {\n  display: flex;\n  color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  padding: 0.1rem 0.75rem;\n  width: 100%;\n  box-sizing: border-box;\n}\n.listbox__3gvMR {\n  max-height: 180px;\n  overflow-y: auto;\n  list-style-type: none;\n  margin: 0;\n  width: 100%;\n  outline: none;\n  padding: 0;\n  box-sizing: border-box;\n}\n.listItem__2T0c1 {\n  width: 100%;\n  --listItem-hover-color: var(--interactive-fill-hover, hsla(216, 24%, 96%, 1));\n  --listItem-selected-color: var(\n    --interactive-fill-active,\n    hsla(220, 17%, 93%, 1)\n  );\n  --listItem-font-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  color: var(--listItem-font-color);\n  padding: 4px 16px;\n  margin: 0;\n  cursor: default;\n  box-sizing: border-box;\n}\n.listWithAvatar__3LzHU {\n  display: flex;\n  align-items: center;\n}\n.nameWithAvatar__2aDlK {\n  margin: 0 5px;\n}\n.selectedItemsSpace__6zKZy {\n  background-color: var(--interactive-fill-default, hsla(0, 0%, 100%, 1));\n  height: auto;\n  width: 100%;\n  max-height: 75px;\n  padding-bottom: 5px;\n  margin-bottom: 4px;\n  overflow-y: auto;\n  box-sizing: border-box;\n}\n.tagItem__1MYj9 {\n  margin: 2px;\n  display: inline-flex;\n  white-space: nowrap;\n}\n.listWithMultiselect__17mia {\n  display: flex;\n  align-items: center;\n}\n\n/* \n* Pointer events none is added to stop event propagation from checkbox container back to the user \n*/\n\n.itemWithMultiselect__3mJ-m {\n  pointer-events: none;\n  display: inline-flex;\n  margin-right: 10px;\n}\n.selectedItem__2FKDf {\n  background-color: var(--listItem-selected-color);\n}\n.activeItem__1bXPe {\n  background-color: var(--listItem-hover-color);\n}\n\n*[dir=\"rtl\"] .itemWithMultiselect__3mJ-m {\n  margin-left: 10px;\n}\n\n@media screen and (max-width: 599px) {\n  .Dropdown__3uVFl {\n    width: 100%;\n  }\n}\n";
var styles = {"Dropdown":"Dropdown__3uVFl","stretch":"stretch__16UHO","l":"l__ImR8M","m":"m__2iTTe","s":"s__2f0O_","SelectionBox":"SelectionBox__1ZdbJ","invalid":"invalid__1Jp2p","placeholder":"placeholder__2D_EI","disabled":"disabled__3u096","selection":"selection__V6aN8","inputFieldWrap":"inputFieldWrap__3KO_v","inputField":"inputField__1bsIH","applyTextColor":"applyTextColor__2iGvN","iconsContainer":"iconsContainer__badVK","Popover":"Popover__2dW1s","popBot":"popBot__fVRh6","popTop":"popTop__BbZTb","caretIcon":"caretIcon__3itFy","emptyState":"emptyState__3bMvZ","listbox":"listbox__3gvMR","listItem":"listItem__2T0c1","listWithAvatar":"listWithAvatar__3LzHU","nameWithAvatar":"nameWithAvatar__2aDlK","selectedItemsSpace":"selectedItemsSpace__6zKZy","tagItem":"tagItem__1MYj9","listWithMultiselect":"listWithMultiselect__17mia","itemWithMultiselect":"itemWithMultiselect__3mJ-m","selectedItem":"selectedItem__2FKDf","activeItem":"activeItem__1bXPe"};
styleInject(css_248z);

var excludeDataAttrs = propsUtil.excludeDataAttrs;

var openMenuOnEnter = function openMenuOnEnter(event, userKeyPress, openMenu) {
  /* this is done to achive keyboard navigation rules set by w3c for listbox.
  https://w3c.github.io/aria-practices/examples/listbox/listbox-collapsible.html
  */
  if (userKeyPress) {
    userKeyPress(event);
  }

  if (event.charCode === 13) {
    openMenu();
  }
};

var SelectionBox = function SelectionBox(_ref) {
  var openMenu = _ref.openMenu,
      isOpen = _ref.isOpen,
      iconClose = _ref.iconClose,
      iconOpen = _ref.iconOpen,
      children = _ref.children,
      referenceRef = _ref.referenceRef,
      className = _ref.className,
      placeholder = _ref.placeholder,
      disabled = _ref.disabled,
      clearSelection = _ref.clearSelection,
      typeahead = _ref.typeahead,
      selectedItem = _ref.selectedItem,
      multiSelect = _ref.multiSelect,
      selectedItemsCount = _ref.selectedItemsCount,
      rest = _objectWithoutProperties(_ref, ["openMenu", "isOpen", "iconClose", "iconOpen", "children", "referenceRef", "className", "placeholder", "disabled", "clearSelection", "typeahead", "selectedItem", "multiSelect", "selectedItemsCount"]);

  var inputRef = /*#__PURE__*/React.createRef();
  var SelectionBoxClass = cn([styles.SelectionBox, className], placeholder ? styles.placeholder : null);
  /**
   * Focus the input element
   */

  var focusInput = function focusInput() {
    return inputRef.current.focus();
  };
  /**
   * Handles the cross icon which renders
   * in case of single dropdown with typeahead
   * @param {object} event
   */


  var handleCross = function handleCross(event) {
    event.stopPropagation();
    clearSelection();
    focusInput();
  };
  /**
   * Invokes Selection Box OnClick
   * which toggles ListBox
   */


  var toggleListBox = function toggleListBox() {
    rest.onClick();
  };
  /**
   * Handles the click of the selection Box
   */


  var handleClick = function handleClick() {
    if (typeahead) {
      focusInput();
    }

    toggleListBox();
  };
  /**
   * This divides some of the props given by
   * getInputProps of downshift , so that appropriate
   * props are given to the input element and the div
   * element of the selection box in case of dropdown
   * with typeahead
   */


  var value = rest.value,
      autoComplete = rest.autoComplete,
      onBlur = rest.onBlur,
      onChange = rest.onChange,
      onKeyDown = rest.onKeyDown,
      id = rest.id,
      typeaheadProps = _objectWithoutProperties(rest, ["value", "autoComplete", "onBlur", "onChange", "onKeyDown", "id"]);

  var typeaheadAndSelectedItems = typeahead && selectedItemsCount > 0 && multiSelect;
  var typeaheadAndSelectedItem = typeahead && selectedItem && !multiSelect;
  /**
   * Decides the placeholder value for input
   * when typeahead.
   */

  var renderInputPlaceholder = function renderInputPlaceholder() {
    return typeaheadAndSelectedItem ? selectedItem : children;
  };
  /**
   * Toggle the placeholder color according to selectedItems or selectedItem
   * when the focus stateof the selection box changes
   */


  var togglePlaceholderColor = function togglePlaceholderColor() {
    return typeaheadAndSelectedItems || typeaheadAndSelectedItem ? styles.applyTextColor : null;
  };
  /**
   * Button to clear selection in case of dropdown with typeahead
   */


  var clearButton = function clearButton() {
    return /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(IconButton, {
      size: "s",
      nude: true,
      icon: /*#__PURE__*/React.createElement(Cross, null),
      onClick: handleCross,
      type: "button",
      "data-testid": "clear-selection"
    }));
  };
  /**
   * Renders the clear icon in dropdown
   */


  var renderClearButton = function renderClearButton() {
    return typeaheadAndSelectedItem ? clearButton() : null;
  };

  var inputClass = cn(styles.inputField, togglePlaceholderColor());

  var getProps = function getProps() {
    return typeahead ? typeaheadProps : rest;
  };
  /**
   * Renders the dropdown with a input element wrapped by a
   * respective div element
   */


  var renderWithTypeahead = function renderWithTypeahead() {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.inputFieldWrap,
      onClick: toggleListBox
    }, /*#__PURE__*/React.createElement("input", _extends({
      className: inputClass,
      disabled: disabled,
      placeholder: renderInputPlaceholder(),
      ref: inputRef
    }, excludeDataAttrs(rest))));
  };

  return /*#__PURE__*/React.createElement("div", _extends({
    className: SelectionBoxClass,
    tabIndex: typeahead || disabled ? -1 : 0,
    role: "button",
    "aria-haspopup": "listbox",
    onKeyPress: function onKeyPress(e) {
      return openMenuOnEnter(e, rest.onKeyPress, openMenu);
    },
    ref: referenceRef
  }, getProps(), {
    onClick: handleClick
  }), typeahead ? renderWithTypeahead() : /*#__PURE__*/React.createElement("div", {
    className: styles.selection
  }, children), /*#__PURE__*/React.createElement("div", {
    className: styles.iconsContainer
  }, renderClearButton(), /*#__PURE__*/React.createElement("span", {
    className: styles.caretIcon
  }, isOpen ? iconOpen : iconClose)));
};

SelectionBox.propTypes = {
  /**
   * Function to open Dropdown
   */
  openMenu: PropTypes.func.isRequired,

  /**
   * Open state of Dropdown
   */
  isOpen: PropTypes.bool,

  /**
   * Icon to show when Dropdown is closed
   */
  iconClose: PropTypes.element.isRequired,

  /**
   * Icon to show when Dropdown is open
   */
  iconOpen: PropTypes.element.isRequired,

  /**
   * Node which SelectionBox wraps
   */
  children: PropTypes.node.isRequired,

  /**
   * Reference of the Popper Reference Element
   */
  referenceRef: PropTypes.func.isRequired,

  /**
   * external classes to be applied to SelectionBox
   */
  className: PropTypes.string.isRequired,

  /**
   * Hint text for dropdown
   */
  placeholder: PropTypes.bool.isRequired,

  /**
   * disable state of dropdown
   */
  disabled: PropTypes.bool,

  /**
   * Clears the selection
   */
  clearSelection: PropTypes.func,

  /**
   * Current SelectedItem
   */
  selectedItem: PropTypes.string,

  /**
   * Multi-select dropdown
   */
  multiSelect: PropTypes.bool,

  /**
   * Count of selectedItems
   */
  selectedItemsCount: PropTypes.number
};
SelectionBox.defaultProps = {
  disabled: false,
  isOpen: false
};

var ListItem = function ListItem(_ref) {
  var active = _ref.active,
      selected = _ref.selected,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, ["active", "selected", "children"]);

  var activeClass = active && styles.activeItem;
  var selectedClass = selected && styles.selectedItem;
  return /*#__PURE__*/React.createElement("li", _extends({
    role: "option",
    className: cn(activeClass, selectedClass, styles.listItem)
  }, rest), children);
};

ListItem.propTypes = {
  /**
   * Active state of item
   */
  active: PropTypes.bool,

  /**
   * Selected state of item
   */
  selected: PropTypes.bool,

  /**
   * Node which ListItem wraps
   */
  children: PropTypes.node.isRequired
};

var sizeClass = function sizeClass(size) {
  switch (size) {
    case "l":
      return styles.l;

    case "m":
      return styles.m;

    case "s":
      return styles.s;

    default:
      return styles.m;
  }
};
/**
 * Sorts the items according to the value
 *
 * @param items items to be sorted
 * @param value input value to match the items with
 * @param key
 *
 * @returns array of filtered items
 */


var filterList = function filterList(items, value) {
  var displayKey = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "key";

  if (value === "") {
    return items;
  }

  return matchSorter(items, value, {
    keys: [displayKey]
  });
};
/**
 * Switches which styles to apply from props
 *
 * @param {object} opts custom props object
 * @returns {string}
 */

var createDropdownClasses = function createDropdownClasses(opts) {
  var truthy = [];

  if (opts.invalid) {
    truthy.push(styles.invalid);
  }

  if (opts.disabled) {
    truthy.push(styles.disabled);
  }

  if (opts.size) {
    truthy.push(sizeClass(opts.size));
  }

  if (opts.stretch) {
    truthy.push(styles.stretch);
  }

  return cn(truthy);
};
/**
 * Creates a function to manage downshift's internal state
 *
 * @param {array} items item objects
 * @returns {function} state reducer function
 */

var createStateReducer = function createStateReducer(items, multiSelect, typeahead, calcPosition) {
  return function (state, changes) {
    /**
     * Manage internal state
     *
     * @function
     * @inner
     * @param {object} state downshift state
     * @param {object} changes current changes to state
     */

    /**
     * We have to use state.selectedItem for the case when we select an item
     * and then close the list as the last selection is captured in state. Also
     * we have to use changes.selected when the list is open and we are selecting as
     * the latest item is captured in changes.
     */
    var lastSelectAndCloseIndex = state.selectedItem && items.findIndex(function (item) {
      return item.id === state.selectedItem.id;
    });
    var lastSelectAndOpenIndex = changes.selectedItem && items.findIndex(function (item) {
      return item.id === changes.selectedItem.id;
    });

    var openAndSelected = function openAndSelected() {
      if (!changes.hasOwnProperty("isOpen")) return false;
      if (lastSelectAndCloseIndex === null) return false;
      return lastSelectAndCloseIndex >= 0 ? true : false;
    };

    var continueSelection = [Downshift.stateChangeTypes.clickItem, Downshift.stateChangeTypes.keyDownEnter];
    var exitSelection = Downshift.stateChangeTypes.blurInput;

    var transformChanges = function transformChanges(changes, values) {
      return _objectSpread2(_objectSpread2({}, changes), values);
    };

    var stayOpenAndSelect = transformChanges(changes, {
      highlightedIndex: lastSelectAndOpenIndex,
      isOpen: true
    });
    var openAfterSelect = transformChanges(changes, {
      highlightedIndex: lastSelectAndCloseIndex
    });
    var clearInput = transformChanges(changes, {
      inputValue: ""
    });
    /**
     * This is for the case when the user is just selecting
     * normally from the dropdown without typing any value in
     * the feild. So this keeps the highlighted index to the last
     * selected item to make it easy for the user to keep selecting
     */

    var clearAndSelect = transformChanges(stayOpenAndSelect, {
      inputValue: ""
    });
    /**
     * This is for the case when an input value has been provided to the
     * feild and the user is done selecting. So the input is cleared and
     * the highlightedIndex is set to first item so that the user can select
     * from top of the list.
     */

    var clearAndResetIndex = transformChanges(clearAndSelect, {
      inputValue: "",
      highlightedIndex: 0
    });

    if (typeahead) {
      /**
       * To fix repositioning of popover in typeahead.
       * While typing in a flipped list and getting no results,
       * the popover would remain flipped with a gap bw dropdown and popover.
       * calcPosition repositions the popover wrt dropdown here.
       * */
      calcPosition.current && calcPosition.current();

      if (multiSelect) {
        if (continueSelection.includes(changes.type)) {
          calcPosition.current && calcPosition.current();
          return state.inputValue !== "" ? clearAndResetIndex : clearAndSelect;
        }

        if (changes.type === exitSelection) {
          return clearInput;
        }
      }
    }

    if (multiSelect) {
      if (continueSelection.includes(changes.type)) {
        calcPosition.current && calcPosition.current();
        return stayOpenAndSelect;
      }

      if (changes.type === exitSelection) {
        return openAfterSelect;
      }
    } // when opening menu, make sure highlighted index is set to
    // selected index but in case of dropdown with typeahead this is not required


    if (openAndSelected() && !typeahead) {
      return openAfterSelect;
    }

    return changes;
  };
};
/**
 * Handle `escape` key behavior
 *
 * @param {object} event downshift event
 * @param {boolean} isOpen menu open/close state
 * @param {function} closeMenu to close the menu
 */

var handleEscapeKey = function handleEscapeKey(event, isOpen, closeMenu) {
  if (event.key !== "Escape") return;
  event.nativeEvent.preventDownshiftDefault = true;
  if (isOpen) closeMenu();
};
/**
 * Handle `arrow` keys behavior.
 * Prevent shifting the highlighted index on open
 * and the event propagation to stop browser defaults
 * of moving the page with arrow keys.
 *
 * @param {object} event downshift event
 * @param {boolean} isOpen menu open/close state
 * @param {function} openMenu to open the menu
 */

var handleArrowKeys = function handleArrowKeys(event, isOpen, openMenu) {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

  if (!isOpen) {
    event.preventDefault();
    event.nativeEvent.preventDownshiftDefault = true;
    openMenu();
  }
};
/**
 * Skips effects of useEffect
 * for initial render and uses
 * effects for further renders
 *
 * @param {function} fn function to be called on didUpdate
 * @param  {...any} inputs input(s) that trigger the function
 */

var useDidUpdateEffect = function useDidUpdateEffect(fn) {
  var didMountRef = useRef(false);

  for (var _len = arguments.length, inputs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    inputs[_key - 1] = arguments[_key];
  }

  useEffect(function () {
    if (didMountRef.current) fn();else didMountRef.current = true;
  }, [].concat(inputs));
};
/**
 * Accepts items and returns items with `selected` true
 *
 * @param {Array} items
 * @returns array
 */

var filterSelectedItems = function filterSelectedItems(items) {
  return items.filter(function (i) {
    return !!i.selected;
  });
};
/**
 * Accepts items and returns first item with `selected` true
 *
 * @param {Array} items
 */

var findSelectedItem = function findSelectedItem(items) {
  return items.find(function (i) {
    return !!i.selected;
  });
};
/**
 * Omits `selected` key from an object.
 *
 * @param {object} input
 * @returns {object}
 */

var omitSelectedKey = function omitSelectedKey(input) {
  if (input && input.hasOwnProperty("selected")) {
    var selected = input.selected,
        ommitted = _objectWithoutProperties(input, ["selected"]);

    return ommitted;
  } else return input;
};

var ListBox = function ListBox(_ref) {
  var innerRef = _ref.innerRef,
      children = _ref.children,
      emptyText = _ref.emptyText,
      size = _ref.size,
      rest = _objectWithoutProperties(_ref, ["innerRef", "children", "emptyText", "size"]);

  var emptyStateClass = cn(styles.emptyState, createDropdownClasses({
    size: size
  }));

  var renderEmptyState = function renderEmptyState() {
    return /*#__PURE__*/React.createElement("div", _extends({
      ref: innerRef,
      className: emptyStateClass,
      role: "listbox"
    }, rest), emptyText);
  };

  var renderList = function renderList() {
    return /*#__PURE__*/React.createElement("ul", _extends({
      ref: innerRef,
      tabIndex: -1,
      role: "listbox",
      className: styles.listbox
    }, rest), children);
  };

  return children.length > 0 ? renderList() : renderEmptyState();
};

ListBox.propTypes = {
  /**
   * Reference of ListBox (Menu)
   */
  innerRef: PropTypes.func,

  /**
   * Node which ListBox wraps
   */
  children: PropTypes.node.isRequired
};

var placementClass = function placementClass() {
  var position = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
  return position.includes("top") ? styles.popTop : styles.popBot;
};

var Popover = function Popover(_ref) {
  var placement = _ref.placement,
      children = _ref.children,
      popoverRef = _ref.popoverRef,
      style = _ref.style,
      elevation = _ref.elevation;
  return /*#__PURE__*/React.createElement("div", {
    className: cn(styles.Popover, placementClass(placement)),
    ref: popoverRef,
    "data-placement": placement,
    style: _objectSpread2(_objectSpread2({}, style), {}, {
      zIndex: elevation
    }),
    "data-testid": "dropdown-popover"
  }, children);
};

Popover.propTypes = {
  /**
   * describes the placement of the popover
   */
  placement: PropTypes.string,

  /**
   * Node which Popover wraps
   */
  children: PropTypes.node.isRequired,

  /**
   * Reference of the popper element
   */
  popoverRef: PropTypes.func.isRequired,

  /**
   * Computed styles by the popper
   */
  style: PropTypes.object.isRequired,

  /**
   * zIndex of Popover
   */
  elevation: PropTypes.number
};
Popover.defaultProps = {
  elevation: 1
};

/**
 * size values
 */
var sizes = ["s", "m", "l"];
/**
 * Warning for illegal initialSelectedItems
 */

var illegalItemsWarning = "There might be a mis-match of id's in items and initalSelectedItems";
/**
 * Warning for illegal initialSelectedItem
 */

var illegalItemWarning = "There might be a mis-match of id's in items and initalSelectedItem";
/**
 * Warning for having multiple items selected with single select
 */

var multiSelectedKeyWarning = "Multiple items found selected without multiSelect prop applied. Add selected:true only to a single item or apply multiSelect for multi-select dropdown";
/**
 * Warning for updating items when using `initalSelectedItems` or `initalSelectedItem`
 */

var legacyUpdateModeWarning = "Do not update items when applying initalSelectedItem or initalSelectedItems. To initially select item(s) add a key selected:true to the item(s) passed initially";
/**
 * Warning for using `reset` with dynamic update mode
 */

var dynamicModeResetWarning = "reset only works with initialSelectedItem or initialSelectedItems. Achieve desired result by updating items dynamically";

var AvatarItem = function AvatarItem(_ref) {
  var avatar = _ref.avatar,
      name = _ref.name;
  var RenderItem = /*#__PURE__*/React.isValidElement(avatar) ? avatar : /*#__PURE__*/React.createElement(Avatar, {
    size: "xxs"
  }, /*#__PURE__*/React.createElement("img", {
    src: avatar.src,
    srcSet: avatar.srcset,
    alt: avatar.alt,
    sizes: avatar.sizes
  }));
  return /*#__PURE__*/React.createElement("span", {
    className: styles.listWithAvatar
  }, RenderItem, /*#__PURE__*/React.createElement("span", {
    className: styles.nameWithAvatar
  }, name));
};

AvatarItem.propTypes = {
  /**
   * avatar to show
   */
  avatar: PropTypes.element.isRequired,

  /**
   * Text rendered with avatar
   */
  name: PropTypes.string.isRequired
};

var SelectableLabel = function SelectableLabel(_ref) {
  var avatar = _ref.avatar,
      checked = _ref.checked,
      name = _ref.name;
  return /*#__PURE__*/React.createElement("span", {
    className: styles.listWithMultiselect
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.itemWithMultiselect
  }, /*#__PURE__*/React.createElement(Checkbox, {
    size: "m",
    checked: checked,
    onChange: function onChange() {
      return null;
    }
  })), avatar ? /*#__PURE__*/React.createElement(AvatarItem, {
    avatar: avatar,
    name: name
  }) : name);
};

SelectableLabel.propTypes = {
  /**
   * avatar to show with the label
   */
  avatar: PropTypes.element,

  /**
   * Text rendered by label
   */
  name: PropTypes.string.isRequired,

  /**
   * checked state of label
   */
  checked: PropTypes.bool.isRequired
};

var Label = function Label(_ref) {
  var avatar = _ref.avatar,
      name = _ref.name;
  return avatar ? /*#__PURE__*/React.createElement(AvatarItem, {
    avatar: avatar,
    name: name
  }) : name;
};

Label.propTypes = {
  /**
   * avatar to show with the label
   */
  avatar: PropTypes.element,

  /**
   * Text rendered by label
   */
  name: PropTypes.string.isRequired
};

var validateSize = propsUtil.validateSize,
    yieldDataAttrs = propsUtil.yieldDataAttrs;
var Dropdown = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var items = _ref.items,
      placeholder = _ref.placeholder,
      onChange = _ref.onChange,
      size = _ref.size,
      stretch = _ref.stretch,
      defaultHighlightedIndex = _ref.defaultHighlightedIndex,
      initialSelectedItem = _ref.initialSelectedItem,
      initialSelectedItems = _ref.initialSelectedItems,
      initialIsOpen = _ref.initialIsOpen,
      elevation = _ref.elevation,
      invalid = _ref.invalid,
      disabled = _ref.disabled,
      displayKey = _ref.displayKey,
      multiSelect = _ref.multiSelect,
      flip = _ref.flip,
      translate = _ref.translate,
      id = _ref.id,
      labelId = _ref.labelId,
      resetRef = _ref.resetRef,
      typeahead = _ref.typeahead,
      emptyText = _ref.emptyText,
      rest = _objectWithoutProperties(_ref, ["items", "placeholder", "onChange", "size", "stretch", "defaultHighlightedIndex", "initialSelectedItem", "initialSelectedItems", "initialIsOpen", "elevation", "invalid", "disabled", "displayKey", "multiSelect", "flip", "translate", "id", "labelId", "resetRef", "typeahead", "emptyText"]);

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      dynamicMode = _React$useState2[0],
      setDynamicMode = _React$useState2[1];
  /**
   * To check if `initialSelectedItem` or `initalSelectedItems` prop are being passed
   */


  var dynamicUpdateMode = !(initialSelectedItem || initialSelectedItems.length);
  /**
   * Check legality of initalSelectedItem
   */

  var legalizeInitialSelectedItem = function legalizeInitialSelectedItem() {
    var matchId = function matchId(item) {
      return item.id === initialSelectedItem.id;
    };

    var legalItem = items.find(matchId);

    if (!legalItem) {
      console.warn(illegalItemWarning);
      return null;
    }

    return legalItem;
  };
  /**
   * Return selected item according to the `selected` key
   * @param {array} items
   * @returns object
   */


  var fetchDynamicSelectedItem = function fetchDynamicSelectedItem(items) {
    if (!multiSelect && filterSelectedItems(items).length > 1) console.warn(multiSelectedKeyWarning);
    return findSelectedItem(items) ? findSelectedItem(items) : null;
  };
  /**
   * When the user passes initalSelectedItem it takes precedence over
   * `selected` key in items and item is set as selected initally
   * after a legalization check. In other case if any item contains
   * `selected` key the first is set as selected initally for the first render
   */


  var allocateInitialSelectedItem = function allocateInitialSelectedItem() {
    return initialSelectedItem ? legalizeInitialSelectedItem() : fetchDynamicSelectedItem(items);
  };

  var legalInitialSelectedItem = allocateInitialSelectedItem();

  var _React$useState3 = React.useState(legalInitialSelectedItem),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      currentSelectedItem = _React$useState4[0],
      setCurrentSelected = _React$useState4[1];
  /**
   * Check legality of initalSelectedItems
   */


  var legalizeInitialSelectedItems = function legalizeInitialSelectedItems() {
    var legalItems = initialSelectedItems.reduce(function (acc, _ref2) {
      var id = _ref2.id;
      var legalItem = items.filter(function (_ref3) {
        var itemId = _ref3.id;
        return itemId === id;
      });

      if (legalItem.length) {
        acc.push(legalItem[0]);
      }

      return acc;
    }, []);
    if (legalItems.length !== initialSelectedItems.length) console.warn(illegalItemsWarning);
    return legalItems;
  };
  /**
   * When the user passes initalSelectedItems it takes precedence over
   * `selected` key in items and those items are set as selected initally
   * after a legalization check. In other case items containing
   * `selected` key are set as selected initally for the first render
   */


  var allocateInitialSelectedItems = function allocateInitialSelectedItems() {
    return initialSelectedItems.length ? legalizeInitialSelectedItems() : filterSelectedItems(items);
  };

  var legalInitialSelectedItems = allocateInitialSelectedItems();

  var _React$useState5 = React.useState(_toConsumableArray(legalInitialSelectedItems)),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      selectedItems = _React$useState6[0],
      setSelectedItems = _React$useState6[1];
  /**
   * Allocate correct value to `currentSelectedItem` according to
   * the value of `selected` key of each item
   */


  var allocateCurrentSelected = function allocateCurrentSelected() {
    return fetchDynamicSelectedItem(items);
  };
  /**
   * Dynamically update `selectedItems` or `currentSelectedItem` according to
   * value of the `selected` key of each item(s). Also set dynamicMode to true when
   * items are updated. Function is wrapped with `useCallback` with a
   * dependency list because the function is invoked in useEffect and might
   * have some side effects if not followed.
   */


  var updateItemsFunction = React.useCallback(function () {
    if (!dynamicUpdateMode) {
      console.warn(legacyUpdateModeWarning);
      return;
    }

    setDynamicMode(true);

    if (multiSelect) {
      setSelectedItems(filterSelectedItems(items));
      setCurrentSelected(null);
    } else {
      setCurrentSelected(allocateCurrentSelected());
    }
  }, [dynamicUpdateMode, multiSelect, allocateCurrentSelected, items]);
  /**
   * Update items after the inital render when the items prop is updated
   */

  useDidUpdateEffect(updateItemsFunction, items);
  /**
   * This is a reference to scheduleUpdate function from react-popper.
   * We need to manually trigger updates for popover position after some events.
   */

  var calcPosition = React.useRef(null);
  /**
   * Return options object
   * @returns object
   */

  var createOpts = function createOpts() {
    var opts = {};
    if (selectedItems && multiSelect) opts.selectedItems = selectedItems.map(function (i) {
      return omitSelectedKey(i);
    });
    if (ref && ref.current) opts.ref = ref;
    return opts;
  };
  /**
   * Calls user provided onChange function
   * with first arg as the current selectedItem
   * and an options object. Function is wrapped with `useCallback` with a
   * dependency list because the function is invoked in useEffect and might
   * have some side effects if not followed.
   */


  var callUserOnChange = React.useCallback(function () {
    if (!onChange || dynamicMode) return null;
    var opts = createOpts();
    onChange(omitSelectedKey(currentSelectedItem), opts);
  }, [onChange, currentSelectedItem, selectedItems]);
  /**
   * Call user onChange
   */

  useDidUpdateEffect(callUserOnChange, currentSelectedItem, selectedItems);

  var TagItem = function TagItem(_ref4) {
    var item = _ref4.item;
    return /*#__PURE__*/React.createElement("div", {
      className: styles.tagItem
    }, /*#__PURE__*/React.createElement(Tag, {
      size: "m",
      dismissible: true,
      onDismiss: function onDismiss() {
        return handleTagDismiss(item);
      },
      "data-testid": "selected-tag"
    }, item[displayKey]));
  };

  if (!validateSize(sizes, size)) {
    size = "m";
    console.warn("size should be one of [".concat(sizes, "]. Using fallback size '").concat(size, "'"));
  }
  /** disabled has been set to both the dropdown and selectionBox for consistent styling
  in disabled state refer to https://source.golabs.io/asphalt/asphalt-react/issues/95.
  As selectionBox has pointer-events: none so we need to provide cursor: not-allowed
  to Dropdown to enable the desired behaviour.
  */
  // These are the properties that are controlled by the selection box


  var selectionBoxClass = createDropdownClasses({
    size: size,
    invalid: invalid,
    disabled: disabled
  }); // The main div controls the width.

  var mainDivClass = cn(styles.Dropdown, createDropdownClasses({
    stretch: stretch,
    disabled: disabled
  }));
  /**
   * Call user translate function if provided
   * or return default implementation
   * @param {number} length
   */

  var handleTranslate = function handleTranslate(length) {
    return translate ? translate(length) : "".concat(length, " Selected");
  };
  /**
   * Handle TagItem dismiss
   * @param {object} item
   */


  var handleTagDismiss = function handleTagDismiss(item) {
    if (!disabled) {
      handleLabelSelection(item);
    }
  };
  /**
   * Handles selection of any Label and set dynamicMode to false
   * @param {object} item
   */


  var handleLabelSelection = function handleLabelSelection(item) {
    setDynamicMode(false);
    setCurrentSelected(item);

    if (multiSelect) {
      handleSelectableLabel(item);
    }
  };
  /**
   * Checks if the item is present in selectedItems
   * @param {object} item
   * @returns {boolean}
   */


  var anyItemSelected = function anyItemSelected(item) {
    return selectedItems.some(function (_ref5) {
      var id = _ref5.id;
      return id === item.id;
    });
  };
  /**
   * Handle Selection of SelectableLabel
   * @param {object} item
   */


  var handleSelectableLabel = function handleSelectableLabel(item) {
    if (anyItemSelected(item)) {
      removeItem(item);
    } else {
      addItem(item);
    }
  };
  /**
   * Decides what is rendered inside SelectionBox
   * @param {object} selectedItem
   */


  var renderSelectionBox = function renderSelectionBox(selectedItem) {
    if (multiSelect) {
      return selectedItems.length ? handleTranslate(selectedItems.length) : placeholder;
    }

    return selectedItem ? renderListItem(selectedItem) : placeholder;
  };
  /**
   * Decides which Label to render as ListItem
   * @param {object} input
   */


  var renderListItem = function renderListItem(input) {
    if (multiSelect) {
      return /*#__PURE__*/React.createElement(SelectableLabel, {
        avatar: input.avatar,
        name: input[displayKey],
        checked: anyItemSelected(input)
      });
    }

    return /*#__PURE__*/React.createElement(Label, {
      avatar: input.avatar,
      name: input[displayKey]
    });
  };
  /**
   * Displays selectedItems as TagItems
   */


  var renderSelected = function renderSelected() {
    return selectedItems.map(function (item, index) {
      return /*#__PURE__*/React.createElement(TagItem, {
        item: item,
        key: index
      });
    });
  };
  /**
   * Add item to selectedItems
   * @param {ibject} item
   */


  var addItem = function addItem(item) {
    return setSelectedItems([].concat(_toConsumableArray(selectedItems), [item]));
  };
  /**
   * Remove item from selectedItems
   * @param {object} item
   */


  var removeItem = function removeItem(item) {
    return setSelectedItems(selectedItems.filter(function (_ref6) {
      var id = _ref6.id;
      return id !== item.id;
    }));
  };
  /**
   * Handle Outer Click on MultiSelect with typeahead
   */


  var handleOuterClick = function handleOuterClick(object) {
    if (multiSelect && typeahead) {
      return object.setState({
        inputValue: ""
      });
    }
  };
  /**
   * Handles the selected prop of ListItem
   * @param {object} item
   * @param {object} selectedItem
   * @returns {boolean}
   */


  var itemSelected = function itemSelected(item, selectedItem) {
    var selected = selectedItem && selectedItem["id"] === item["id"];
    var selectedMulti = selected && selectedItems.includes(item);

    if (multiSelect) {
      return selectedMulti;
    }

    return selected;
  };
  /**
   * Renders the ItemList.
   * If the dropdown resets or a deprecating prop is passed
   * returns current/old set of items else returns updated items
   * in the list
   *
   * @param {String} inputValue
   */


  var renderItemList = function renderItemList(inputValue) {
    return !typeahead ? items : filterList(items, inputValue, displayKey);
  };
  /**
   * In case of multiSelect and typeahead the selectedItem state of downshift is not updated.
   * This is done on purpose, because the inputValue is controlled by selectedItem's value which if
   * updated as other cases will break the previous selection behaviour of Dropdown. This does
   * not affect the user as the user is still gets expected results.
   */


  var selectedController = multiSelect && typeahead ? null : currentSelectedItem;
  var dataAttrs = yieldDataAttrs(rest);
  return /*#__PURE__*/React.createElement(Downshift, {
    defaultHighlightedIndex: typeahead ? 0 : defaultHighlightedIndex,
    itemToString: function itemToString(item) {
      return item ? item[displayKey] : "";
    },
    stateReducer: createStateReducer(items, multiSelect, typeahead, calcPosition),
    initialSelectedItem: legalInitialSelectedItem,
    initialIsOpen: initialIsOpen,
    onSelect: function onSelect(item) {
      return handleLabelSelection(item);
    },
    id: id,
    labelId: labelId,
    onOuterClick: handleOuterClick,
    supplementary: {
      multiSelect: multiSelect,
      initialSelectedItems: legalInitialSelectedItems,
      selectedItems: selectedItems,
      setSelectedItems: setSelectedItems,
      setCurrentSelected: setCurrentSelected,
      dynamicUpdateMode: dynamicUpdateMode
    },
    selectedItem: selectedController,
    ref: resetRef
  }, function (_ref7) {
    var getInputProps = _ref7.getInputProps,
        getItemProps = _ref7.getItemProps,
        getMenuProps = _ref7.getMenuProps,
        isOpen = _ref7.isOpen,
        openMenu = _ref7.openMenu,
        closeMenu = _ref7.closeMenu,
        toggleMenu = _ref7.toggleMenu,
        highlightedIndex = _ref7.highlightedIndex,
        selectedItem = _ref7.selectedItem,
        clearSelection = _ref7.clearSelection,
        inputValue = _ref7.inputValue;

    var renderListBox = function renderListBox() {
      return /*#__PURE__*/React.createElement(ListBox, _extends({}, getMenuProps({
        refKey: "innerRef",
        "aria-multiselectable": !!multiSelect,
        "data-testid": "listbox"
      }), {
        emptyText: emptyText,
        size: size
      }), renderItemList(inputValue).map(function (item, index) {
        return /*#__PURE__*/React.createElement(ListItem, _extends({
          key: index
        }, getItemProps({
          key: index,
          index: index,
          item: item,
          active: highlightedIndex === index,
          selected: itemSelected(item, selectedItem)
        })), renderListItem(item));
      }));
    };

    return /*#__PURE__*/React.createElement("div", _extends({
      className: mainDivClass,
      "aria-disabled": disabled
    }, dataAttrs), multiSelect && selectedItems.length ? /*#__PURE__*/React.createElement("div", {
      className: styles.selectedItemsSpace,
      onClick: closeMenu,
      "data-testid": "selected-items"
    }, renderSelected()) : null, /*#__PURE__*/React.createElement(Manager, null, /*#__PURE__*/React.createElement(Reference, null, function (refProps) {
      return /*#__PURE__*/React.createElement(SelectionBox, _extends({}, getInputProps({
        "data-testid": "selection-box",
        onKeyDown: function onKeyDown(event) {
          handleEscapeKey(event, isOpen, closeMenu);
          handleArrowKeys(event, isOpen, openMenu);
        }
      }), {
        onClick: toggleMenu,
        isOpen: isOpen,
        iconClose: /*#__PURE__*/React.createElement(CaretDown, null),
        iconOpen: /*#__PURE__*/React.createElement(CaretUp, null),
        openMenu: openMenu,
        referenceRef: refProps.ref,
        className: selectionBoxClass,
        placeholder: !(selectedItem || selectedItems.length),
        disabled: disabled,
        typeahead: typeahead,
        clearSelection: clearSelection,
        selectedItem: selectedItem ? selectedItem[displayKey] : null,
        multiSelect: multiSelect,
        selectedItemsCount: selectedItems.length
      }), renderSelectionBox(selectedItem));
    }), isOpen ? /*#__PURE__*/React.createElement(Popper, {
      placement: "bottom-start",
      modifiers: {
        preventOverflow: {
          escapeWithReference: true
        },
        flip: {
          enabled: !multiSelect && flip
        }
      }
    }, function (_ref8) {
      var ref = _ref8.ref,
          style = _ref8.style,
          placement = _ref8.placement,
          scheduleUpdate = _ref8.scheduleUpdate;
      calcPosition.current = scheduleUpdate;
      return /*#__PURE__*/React.createElement(Popover, {
        popoverRef: ref,
        style: style,
        placement: placement,
        elevation: elevation
      }, renderListBox());
    }) : null));
  });
});
Dropdown.propTypes = {
  /**
   * Array of Objects that dropdown renders
   * ```
   * items = [{key: "jakarta", key: "1"}, ...]
   * ```
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    key: PropTypes.string
  }).isRequired).isRequired,

  /**
   * Hint text to show when no item is selected
   */
  placeholder: PropTypes.string,

  /**
   *
   * Called when selected item changes by user interaction or the dropdown resets.
   * It receives multiple arguments with the following signature
   *
   * - item - item selected by user interaction
   * - options(object) - `selectedItems` key gives an array of selectedItems when multiSelect
   *
   * ```
   *  (item, options) => void
   * ```
   *
   */
  onChange: PropTypes.func,

  /**
   * renders the component with the provided size. accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Stretches the component width to match its container
   */
  stretch: PropTypes.bool,

  /**
   * This is the index value of an item which will be highlighted when dropdown is initialized or reset
   */
  defaultHighlightedIndex: PropTypes.number,

  /**
   * Item which should be pre-selected on initialization. Applies for the first render only.
   *
   * **Note**: _This prop is going to be deprecated in next major version_
   */
  initialSelectedItem: PropTypes.object,

  /**
   * Items which should be pre-selected for multiSelect on initialization. Applies for the first render only.
   *
   * **Note**: _This prop is going to be deprecated in next major version_
   */
  initialSelectedItems: PropTypes.arrayOf(PropTypes.object),

  /**
   * Opens dropdown on initialization
   */
  initialIsOpen: PropTypes.bool,

  /**
   * z-index of list box
   */
  elevation: PropTypes.number,

  /**
   * Disables the dropdown
   */
  disabled: PropTypes.bool,

  /**
   * Applies invalid styles to the dropdown
   */
  invalid: PropTypes.bool,

  /**
   *
   * Key property name of the items, the key property's value will be shown as the list item .
   */
  displayKey: PropTypes.string,

  /**
   * Dropdown flip according to viewport boundary
   */
  flip: PropTypes.bool,

  /**
   * Enables multiple item selection
   */
  multiSelect: PropTypes.bool,

  /**
   * Function to show custom text in selectionBox in multiSelect
   *
   */
  translate: PropTypes.func,

  /**
   * id of the dropdown
   */
  id: PropTypes.string,

  /**
   * Id of the label used for dropdown. Used for aria attributes.
   */
  labelId: PropTypes.string,

  /**
   * Custom reference for reset.
   *
   *_`reset` does not clear the dropdown.
   * It resets the dropdown to its first render.
   * Only works with `initialSelectedItem` or `initialSelectedItems`_
   *
   * **Note**: _This prop is going to be deprecated in next major version_
   */
  resetRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),

  /**
   * Enables a typeahead / search
   */
  typeahead: PropTypes.bool,

  /**
   * Show custom text when no result is found
   */
  emptyText: PropTypes.string
};
Dropdown.defaultProps = {
  size: "m",
  stretch: false,
  placeholder: "Select",
  elevation: 1,
  displayKey: "key",
  disabled: false,
  defaultHighlightedIndex: 0,
  flip: true,
  multiSelect: false,
  initialIsOpen: false,
  initialSelectedItems: [],
  typeahead: false,
  emptyText: "No Results Found."
};

/**
 * This is a highly experimental function which enables the user to reset the dropdown
 * to it's initial state.
 *
 * @param {function} ref reference of dropdown to reset
 *
 */

var reset = function reset(ref) {
  if (!ref || !ref.current) return;
  /**
   * downshift props
   */

  var _ref$current$props = ref.current.props,
      initialSelectedItem = _ref$current$props.initialSelectedItem,
      initialIsOpen = _ref$current$props.initialIsOpen,
      defaultHighlightedIndex = _ref$current$props.defaultHighlightedIndex,
      inputValue = _ref$current$props.inputValue;
  /**
   * supplementary props
   */

  var _ref$current$props$su = ref.current.props.supplementary,
      multiSelect = _ref$current$props$su.multiSelect,
      initialSelectedItems = _ref$current$props$su.initialSelectedItems,
      setSelectedItems = _ref$current$props$su.setSelectedItems,
      setCurrentSelected = _ref$current$props$su.setCurrentSelected,
      dynamicUpdateMode = _ref$current$props$su.dynamicUpdateMode;
  /**
   * This function informs the dropdown
   * when `reset` is called
   */

  var handleMultiReset = function handleMultiReset() {
    setSelectedItems(_toConsumableArray(initialSelectedItems));
    setCurrentSelected(null);
  };

  if (dynamicUpdateMode) {
    console.warn(dynamicModeResetWarning);
  }
  /**
   * Downshift's `selecteditems` is now controlled.
   *
   */


  var handleReset = function handleReset() {
    initialSelectedItem ? setCurrentSelected(initialSelectedItem) : setCurrentSelected(null);
    ref.current.reset({
      highlightedIndex: defaultHighlightedIndex ? defaultHighlightedIndex : null,
      isOpen: initialIsOpen,
      inputValue: inputValue || ""
    });
  };

  if (multiSelect) {
    handleMultiReset();
  } else {
    handleReset();
  }
};

export default Dropdown;
export { reset };
